import { useEffect, useRef, useState } from 'react';
import { YEAR_COUNT_UP_MS } from '@hitster/shared';

/**
 * Zaehlt eine Jahreszahl animiert auf ihren Zielwert zu.
 *
 * Gedacht fuer den Moment des Aufdeckens -- den, auf den in jeder Runde
 * alle warten. Eine eingeblendete Zahl ist sofort da; eine laufende
 * gibt dem Moment die Spannung, die er verdient.
 *
 * Drei Entscheidungen machen daraus mehr als einen Zaehler:
 *
 *  - Die Richtung wechselt zufaellig. Laeuft die Zahl immer von unten
 *    heran, weiss man nach drei Runden, dass das Ziel oberhalb des
 *    Startwerts liegt -- die Bewegung verraet das Ergebnis.
 *  - Die Schrittweiten sind ungleich. Ein gleichmaessiger Zaehler wirkt
 *    mechanisch; unregelmaessige Spruenge wirken wie ein Zeiger, der
 *    sich sucht.
 *  - Am Ende wird stark langsamer. Die letzten Jahre ticken einzeln,
 *    und genau dort entsteht die Spannung.
 */

/** Wie weit vor dem Ziel der Zaehler ansetzt -- zufaellig in dieser Spanne. */
const RUN_UP_MIN_YEARS = 18;
const RUN_UP_MAX_YEARS = 46;

/** Groesse eines einzelnen Sprungs, zufaellig in dieser Spanne. */
const STEP_MIN_YEARS = 1;
const STEP_MAX_YEARS = 5;

/**
 * Bewusst lang: knapp zwei Sekunden lassen den Raum "ooooh" machen.
 * Vorher 700ms -- das war vorbei, bevor der Blick die Zahl gefunden
 * hatte.
 *
 * Der Wert kommt aus dem geteilten Code, weil der Server ihn fuer
 * "automatisch weiter" braucht. Stuende er nur hier, wuerde jede
 * Aenderung dem Host unbemerkt Lesezeit wegnehmen.
 */
const DEFAULT_DURATION_MS = YEAR_COUNT_UP_MS;

/**
 * Stark auslaufend: 1 - (1-t)^4.
 *
 * Der Grossteil der Strecke ist frueh erledigt, die letzten Jahre
 * brauchen dann auffaellig lange. Ein linearer Verlauf oder ein
 * schwaecheres Auslaufen nimmt dem Aufdecken die Pointe.
 */
function easeOutQuart(t: number): number {
  return 1 - Math.pow(1 - t, 4);
}

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

function randomInt(min: number, max: number, random: () => number): number {
  return min + Math.floor(random() * (max - min + 1));
}

/**
 * Baut die Folge der angezeigten Zahlen, von einem zufaelligen Anlauf
 * bis exakt auf das Ziel.
 *
 * Exportiert, damit die Folge pruefbar ist, ohne Bilder zu zaehlen: die
 * Eigenschaften, auf die es ankommt (endet auf dem Ziel, laeuft mal
 * hinauf und mal hinab, springt ungleichmaessig), lassen sich nur hier
 * sinnvoll festhalten.
 */
export function buildCountStops(target: number, random: () => number): number[] {
  const distance = randomInt(RUN_UP_MIN_YEARS, RUN_UP_MAX_YEARS, random);
  /** Von neuerem Jahr herab oder von aelterem herauf -- je zur Haelfte. */
  const downwards = random() < 0.5;

  const from = downwards ? target + distance : target - distance;
  const stops = [from];

  let current = from;
  while (current !== target) {
    const step = randomInt(STEP_MIN_YEARS, STEP_MAX_YEARS, random);
    // Nie ueber das Ziel hinaus -- die letzte Zahl MUSS das Ziel sein.
    current = downwards ? Math.max(target, current - step) : Math.min(target, current + step);
    stops.push(current);
  }

  return stops;
}

export interface CountUp {
  /** Die aktuell anzuzeigende Zahl. */
  value: number;
  /**
   * Die Zahl steht endgueltig.
   *
   * Der Aufrufer kann daran alles haengen, was das Ergebnis verraet.
   * Wuerde "richtig" oder "falsch" schon waehrend des Laufs stehen,
   * waere der Zaehler nur noch Dekoration -- man liest das Urteil und
   * schaut der Zahl nicht mehr zu.
   */
  settled: boolean;
}

export function useCountUp(
  target: number,
  options: {
    durationMs?: number;
    random?: () => number;
    /**
     * Wird bei jedem SICHTBAREN Wechsel der Zahl aufgerufen, mit dem
     * Fortschritt 0..1.
     *
     * Ausdruecklich nicht pro Bild: bei 60 Bildern pro Sekunde und
     * knapp zwei Sekunden waeren das ueber hundert Aufrufe, aber nur
     * ein Dutzend Zahlenwechsel. Fuer ein Geraeusch pro Sprung ist der
     * Unterschied der ganze Punkt.
     */
    onStep?: (progress: number) => void;
  } = {},
): CountUp {
  const { durationMs = DEFAULT_DURATION_MS, random = Math.random } = options;

  const [value, setValue] = useState(target);
  const [settled, setSettled] = useState(false);
  const frameRef = useRef<number | null>(null);

  /**
   * Die Zufallsquelle darf die Animation nicht neu starten.
   *
   * Wird `random` als Inline-Funktion uebergeben, wechselt bei jedem
   * Rendern die Identitaet. Steht sie in den Abhaengigkeiten, wuerde
   * der Effekt endlos neu laufen und die Zahl nie stehenbleiben.
   */
  const randomRef = useRef(random);
  randomRef.current = random;

  // Gleicher Grund wie bei `random`: darf den Effekt nicht neu starten.
  const onStepRef = useRef(options.onStep);
  onStepRef.current = options.onStep;

  useEffect(() => {
    /**
     * Wer Bewegung abbestellt hat, bekommt die Zahl sofort -- und damit
     * auch das Ergebnis. Spannung darf nicht auf Kosten der
     * Bedienbarkeit gehen.
     */
    if (prefersReducedMotion()) {
      setValue(target);
      setSettled(true);
      return;
    }

    const stops = buildCountStops(target, randomRef.current);
    const start = performance.now();
    setValue(stops[0] as number);
    setSettled(false);

    /**
     * Letzter gemeldeter Index -- damit `onStep` nur bei einem echten
     * Zahlenwechsel feuert und nicht bei jedem Bild.
     */
    let lastIndex = 0;

    const tick = (now: number) => {
      const progress = Math.min(1, (now - start) / durationMs);
      const eased = easeOutQuart(progress);

      // Bei progress = 1 ergibt das den letzten Eintrag: das Ziel.
      const index = Math.min(stops.length - 1, Math.floor(eased * stops.length));

      if (index !== lastIndex) {
        lastIndex = index;
        setValue(stops[index] as number);
        onStepRef.current?.(progress);
      }

      if (progress < 1) frameRef.current = requestAnimationFrame(tick);
      else setSettled(true);
    };

    frameRef.current = requestAnimationFrame(tick);

    return () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    };
  }, [target, durationMs]);

  return { value, settled };
}
